import React, { useEffect, useMemo, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { AppSelect as Select } from '../../../../../../../components/Select'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import { TruncatedValue } from '../../../../../../common/TruncatedValue'
import { createOverpayment, getOverpaymentReasonLookup } from '../../core/api'
import { overpaymentCreateErrorText } from '../../core/errors'
import { OVERPAYMENT_CREATE_ERROR_FALLBACK } from '../../core/consts'
import {
  OverpaymentHistoryItem,
  OverpaymentRecord,
  OverpaymentReasonLookupItem,
} from '../../core/response.types'
import { OverpaymentHistoryTable } from '../OverpaymentHistoryTable/OverpaymentHistoryTable'

export interface OverpaymentModalContext {
  matchingTransactionId: number
  amount: string | number
  currency: string
  paymentMethod: string
  requisite: string
  recipient: { externalId: string; merchantToken: string }
  culprit: { externalId: string; merchantToken: string }
  // Previous overpayments of the deposit, newest first.
  history?: OverpaymentHistoryItem[]
}

interface Props {
  show: boolean
  onClose: () => void
  context: OverpaymentModalContext
  onCreated?: (record: OverpaymentRecord) => void
}

interface FormShape {
  reasonId: number | ''
  comment: string
}

const schema = Yup.object().shape({
  reasonId: Yup.number().integer().min(1).required('Выберите причину'),
  comment: Yup.string().max(1000, 'Не более 1000 символов'),
})

export const OverpaymentModal: React.FC<Props> = ({ show, onClose, context, onCreated }) => {
  const [reasons, setReasons] = useState<OverpaymentReasonLookupItem[]>([])
  const [generalError, setGeneralError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const previous = context.history ?? []

  const formik = useFormik<FormShape>({
    initialValues: { reasonId: '', comment: '' },
    validationSchema: schema,
    enableReinitialize: false,
    onSubmit: async (values) => {
      setSubmitting(true)
      setGeneralError(null)
      try {
        const response = await createOverpayment({
          sourceMatchingTransactionId: context.matchingTransactionId,
          reasonId: Number(values.reasonId),
          ...(values.comment ? { comment: values.comment } : {}),
        })
        if (response.data?.success && response.data.data) {
          onCreated?.(response.data.data)
          onClose()
          return
        }
        setGeneralError(OVERPAYMENT_CREATE_ERROR_FALLBACK)
      } catch (e: unknown) {
        setGeneralError(overpaymentCreateErrorText(e))
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (!show) {
      formik.resetForm()
      setGeneralError(null)
      return
    }
    getOverpaymentReasonLookup().then((res) => {
      if (res.data?.success && res.data.data) {
        setReasons(res.data.data)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  const reasonOptions = useMemo(() => reasons, [reasons])
  const currentReason = reasonOptions.find((r) => r.value === formik.values.reasonId) ?? null

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Создать переоплату</Modal.Title>
      </Modal.Header>
      <form onSubmit={formik.handleSubmit}>
        <Modal.Body>
          {previous.length > 0 && (
            <div className='mb-4'>
              <div className='alert alert-warning py-2 mb-2 fw-semibold'>
                По этому депозиту уже была переоплата
              </div>
              {/* The table sits outside the alert: body-colour text on the warning fill is not
                  guaranteed to hold contrast in both themes. */}
              <div className='border rounded p-2'>
                <OverpaymentHistoryTable items={previous} />
              </div>
            </div>
          )}
          <ReadOnlyRow label='Сумма' value={`${context.amount} ${context.currency}`} />
          <ReadOnlyRow label='Метод' value={context.paymentMethod} />
          <ReadOnlyRow label='Реквизит' value={context.requisite} />
          <ReadOnlyRow label='Получатель' value={<ParticipantValue {...context.recipient} />} />
          <ReadOnlyRow label='Виновник' value={<ParticipantValue {...context.culprit} />} />

          <div className='mb-3'>
            <label className='form-label required'>Причина</label>
            <Select<OverpaymentReasonLookupItem>
              options={reasonOptions}
              value={currentReason}
              onChange={(opt) => formik.setFieldValue('reasonId', opt?.value ?? '')}
              placeholder='Выберите причину'
              isClearable={false}
              classNamePrefix='overpayment-reason'
              aria-label='reason-select'
            />
            {formik.touched.reasonId && formik.errors.reasonId && (
              <div className='text-danger small'>{formik.errors.reasonId as string}</div>
            )}
          </div>

          <div className='mb-3'>
            <label className='form-label'>Комментарий</label>
            <textarea
              className='form-control'
              rows={3}
              maxLength={1000}
              {...formik.getFieldProps('comment')}
            />
            {formik.touched.comment && formik.errors.comment && (
              <div className='text-danger small'>{formik.errors.comment as string}</div>
            )}
          </div>

          {generalError && <div className='alert alert-danger py-2'>{generalError}</div>}
        </Modal.Body>
        <Modal.Footer>
          <button type='button' className='btn btn-light' onClick={onClose} disabled={submitting}>
            Отмена
          </button>
          <button type='submit' className='btn btn-primary' disabled={submitting}>
            {submitting ? 'Создаём…' : previous.length > 0 ? 'Создать повторную' : 'Создать'}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

interface ReadOnlyRowProps {
  label: string
  value: React.ReactNode
}

// Сворачивается только идентификатор клиента: токен мерчанта короткий и нужен целиком,
// а маска по всей склеенной строке отрезала бы именно его.
const ParticipantValue: React.FC<{ externalId: string; merchantToken: string }> = ({
  externalId,
  merchantToken,
}) => (
  <>
    <TruncatedValue value={externalId} />
    {' · '}
    {merchantToken}
  </>
)

const ReadOnlyRow: React.FC<ReadOnlyRowProps> = ({ label, value }) => (
  <div className='mb-2'>
    <div className='text-muted small'>{label}</div>
    <div>{value}</div>
  </div>
)
